// Full-width /chat surface. Same JSON endpoints as the bubble (POST
// /chats, GET /chats/:id) — different DOM (full-width column, in-page
// composer with auto-grow textarea, model picker, suggested prompts,
// conversations rail).
//
// DOM contract: every row inserted into `messages` is built by
// app/javascript/lib/chat_render.js. The controller stays focused on
// state + network; rendering lives in chat_render so the bubble and
// page surfaces stay visually identical.
//
// Source of truth for behavior:
//   ~/Developer/work/autoboost/redesign/chat-design/chat-app.jsx
// — Composer (auto-grow + Enter to send + Shift+Enter newline + stop
//   button while generating), ColdStart suggested prompts (pickSuggestion),
//   ConversationsRail click-to-switch.

import { Controller } from "@hotwired/stimulus"
import {
  buildMessageRow,
  buildToolLedger,
  buildThinkingRow,
  buildDaySeparator,
  csrfToken,
} from "../lib/chat_render"

export default class extends Controller {
  static targets = [
    "messages",        // scrollable conversation container
    "input",           // <textarea>
    "sendButton",      // <button> Send
    "sendLabel",       // <span> inside the Send button (toggles "Send" ↔ "Stop")
    "hint",            // composer hint text
    "modelSelect",     // <select> model picker
    "rateBanner",      // hidden by default; shown on 429
  ]
  static values = {
    createUrl: String,        // POST /chats
    showUrl: String,          // GET /chats/:id (prefix)
    conversationId: String,   // current conversation id, mutable
    initialModel: String,     // selected model on first render
    pageBaseUrl: String,      // /chat — used for history push on switch
    userId: String,           // current user id — for the thinking-row SSE target
  }

  // ---------- lifecycle ----------

  connect() {
    this.conversationId = this.hasConversationIdValue && this.conversationIdValue.length > 0
      ? this.conversationIdValue
      : null
    this.inflight = false
    console.log(`[chat-panel] connect: createUrl=${this.createUrlValue} conversationId=${this.conversationId || "(none)"} model=${this.initialModelValue}`)
    if (this.conversationId) {
      this.loadHistory()
    }
    // Focus composer on initial paint so the user can just start typing.
    requestAnimationFrame(() => {
      if (this.hasInputTarget) this.inputTarget.focus()
    })
    // Send is disabled while empty; flip as soon as there's content
    // (whether typed by the user or auto-injected by the transcribe
    // controller — which dispatches an `input` event after each
    // partial/final, triggering this same method).
    this.updateSendEnabled()
  }

  // ---------- history ----------

  async loadHistory() {
    if (!this.conversationId) return
    const url = `${this.showUrlValue}/${this.conversationId}`
    console.log(`[chat-panel] loadHistory: GET ${url}`)
    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
      })
      console.log(`[chat-panel] loadHistory response status=${response.status}`)
      if (!response.ok) {
        this.conversationId = null
        return
      }
      const data = await response.json()
      console.log(`[chat-panel] loadHistory: ${data.messages?.length || 0} message rows`)
      this.clearMessages()
      this.renderConversation(data.messages || [])
    } catch (e) {
      console.error("[chat-panel] loadHistory error:", e)
    }
  }

  // Render an array of ChatMessage payload rows. Tool rows are grouped
  // into a ToolLedger above the FOLLOWING assistant turn (matches the
  // React design's MidConversation pattern: user → tool* → assistant
  // → end of round).
  renderConversation(messages) {
    let pendingTools = []
    messages.forEach((msg) => {
      if (msg.role === "tool") {
        pendingTools.push(msg)
        return
      }
      if (pendingTools.length && msg.role === "assistant") {
        const ledger = buildToolLedger(pendingTools)
        if (ledger) this.messagesInner().appendChild(ledger)
        pendingTools = []
      }
      const row = buildMessageRow(msg)
      if (row) this.messagesInner().appendChild(row)
    })
    // Trailing orphan tools (shouldn't happen but render them so they
    // don't silently disappear).
    if (pendingTools.length) {
      const ledger = buildToolLedger(pendingTools)
      if (ledger) this.messagesInner().appendChild(ledger)
    }
    this.scrollToBottom()
  }

  // ---------- sending ----------

  // Stimulus action — click on the Send button.
  send(event) {
    event?.preventDefault?.()
    if (this.inflight) {
      this.stop()
      return
    }
    this.sendCurrent()
  }

  // Keyboard from the composer textarea.
  // Enter alone = send. Shift+Enter = newline (default behavior).
  enterPressed(event) {
    if (event.shiftKey) return // allow newline
    event.preventDefault()
    if (!this.inflight) this.sendCurrent()
  }

  async sendCurrent() {
    // Notify any composer add-ons (transcribe controller, etc.) that a
    // send is about to fire so they can flush state into the textarea
    // BEFORE we read its value. Synchronous handlers only — no await.
    window.dispatchEvent(new CustomEvent("chat:before-send", { bubbles: true }))

    const content = this.inputTarget.value.trim()
    if (!content) return

    this.appendUserMessage(content)
    this.inputTarget.value = ""
    this.autoGrow()
    this.beginInflight()
    this.updateSendEnabled()

    try {
      const model = this.hasModelSelectTarget ? this.modelSelectTarget.value : null
      console.log(`[chat-panel] send: POST ${this.createUrlValue} chars=${content.length} model=${model || "(default)"}`)
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          message: content,
          conversation_id: this.conversationId,
          model: model,
          source: "page",
        }),
      })
      console.log(`[chat-panel] send response status=${response.status}`)

      if (response.status === 429) {
        this.showRateBanner()
        return
      }
      const data = await response.json()
      if (!response.ok) {
        this.appendErrorMessage(data.error || "Unable to reach the assistant. Please retry.")
        return
      }

      if (data.conversation_id) {
        this.conversationId = String(data.conversation_id)
      }
      if (data.model && this.hasModelSelectTarget) {
        this.modelSelectTarget.value = data.model
      }
      // Drop the thinking row before rendering the result.
      this.removeThinkingRow()
      this.appendAssistantRound(data.tool_messages || [], data.message)
    } catch (e) {
      console.error("[chat-panel] send error:", e)
      this.removeThinkingRow()
      this.appendErrorMessage("Network error. Please retry.")
    } finally {
      this.endInflight()
      if (this.hasInputTarget) this.inputTarget.focus()
    }
  }

  // Stop button — placeholder. We don't have a backend cancellation
  // contract yet; the user clicking Stop just resets the UI. The
  // current generation completes server-side regardless.
  stop() {
    console.log("[chat-panel] stop pressed (UI-only — no backend cancel yet)")
    this.removeThinkingRow()
    this.endInflight()
  }

  // ---------- rail / conversation switching ----------

  // Stimulus action — click on a rail row.
  switchConversation(event) {
    event.preventDefault()
    const link = event.currentTarget
    const id = link.dataset.conversationId
    if (!id || String(this.conversationId) === String(id)) return
    console.log(`[chat-panel] switchConversation to=${id}`)
    this.conversationId = String(id)
    this.clearMessages()
    this.loadHistory()
    // Update active state on rail rows.
    this.element.querySelectorAll(".chat-rail-item.active").forEach((el) => el.classList.remove("active"))
    link.classList.add("active")
    // Update URL so a refresh stays on the same conversation.
    if (this.hasPageBaseUrlValue) {
      const url = `${this.pageBaseUrlValue}?c=${this.conversationId}`
      window.history.pushState({}, "", url)
    }
  }

  // Stimulus action — "New chat" link / button.
  newChat(event) {
    event?.preventDefault?.()
    console.log("[chat-panel] newChat")
    this.conversationId = null
    this.clearMessages()
    // Re-render the cold-start state inline so the user can immediately
    // pick a suggested prompt without a page reload.
    this.messagesInner().innerHTML = this.coldStartHtml()
    this.bindColdStart()
    this.element.querySelectorAll(".chat-rail-item.active").forEach((el) => el.classList.remove("active"))
    if (this.hasPageBaseUrlValue) {
      window.history.pushState({}, "", this.pageBaseUrlValue)
    }
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  // ---------- suggested prompts ----------

  pickSuggestion(event) {
    event.preventDefault()
    const prompt = event.currentTarget.dataset.prompt
    if (!prompt) return
    console.log(`[chat-panel] pickSuggestion: ${prompt}`)
    this.inputTarget.value = prompt
    this.autoGrow()
    this.sendCurrent()
  }

  // ---------- model picker ----------

  modelChanged(event) {
    console.log(`[chat-panel] modelChanged to=${event.currentTarget.value}`)
    // The actual override is applied on the NEXT send (the server
    // persists model on the conversation when received). Nothing to
    // POST eagerly — keeps the contract minimal.
  }

  // ---------- composer behaviors ----------

  autoGrow() {
    const ta = this.inputTarget
    if (!ta) return
    ta.style.height = "auto"
    ta.style.height = Math.min(200, ta.scrollHeight) + "px"
  }

  // Send is disabled while empty and enabled once the textarea has
  // any non-whitespace content. Wired to the textarea's `input`
  // event in the composer partial so it also fires when the
  // transcribe controller streams partials into the textarea.
  updateSendEnabled() {
    if (!this.hasSendButtonTarget || !this.hasInputTarget) return
    if (this.inflight) return
    const hasContent = this.inputTarget.value.trim().length > 0
    this.sendButtonTarget.disabled = !hasContent
  }

  beginInflight() {
    this.inflight = true
    if (this.hasInputTarget) this.inputTarget.disabled = true
    if (this.hasSendLabelTarget) this.sendLabelTarget.textContent = "Stop"
    if (this.hasSendButtonTarget) this.sendButtonTarget.classList.add("stop")
    if (this.hasHintTarget) this.hintTarget.textContent = "Generating reply…"
    // Drop a Thinking row at the bottom of the messages list. The id
    // matches the broadcast target ChatRunner#broadcast_status hits
    // via Mercure, so the label text swaps as the turn progresses
    // ("Pulling your campaigns" → "Composing reply" → …).
    const thinking = buildThinkingRow("Thinking…")
    thinking.dataset.thinking = "1"
    if (this.hasUserIdValue && this.userIdValue) {
      thinking.id = `chat_thinking_user_${this.userIdValue}`
    }
    this.messagesInner().appendChild(thinking)
    this.scrollToBottom()
  }

  endInflight() {
    this.inflight = false
    if (this.hasInputTarget) this.inputTarget.disabled = false
    if (this.hasSendLabelTarget) this.sendLabelTarget.textContent = "Send"
    if (this.hasSendButtonTarget) this.sendButtonTarget.classList.remove("stop")
    if (this.hasHintTarget) this.hintTarget.innerHTML = "<kbd>↵</kbd> to send · <kbd>⇧↵</kbd> for newline"
    this.updateSendEnabled()
  }

  removeThinkingRow() {
    const inner = this.messagesInner()
    if (!inner) return
    inner.querySelectorAll('[data-thinking="1"]').forEach((el) => el.remove())
  }

  // ---------- rendering helpers (delegating to chat_render) ----------

  // If we're rendering the cold-start state, replace it with an empty
  // wrapper before appending real rows.
  ensureRoundContainer() {
    const inner = this.messagesInner()
    const coldStart = inner.querySelector(".chat-cold-start")
    if (coldStart) coldStart.remove()
  }

  appendUserMessage(content) {
    this.ensureRoundContainer()
    const row = buildMessageRow({ role: "user", content })
    if (row) {
      const round = document.createElement("div")
      round.className = "chat-round"
      round.dataset.openRound = "1"
      round.appendChild(row)
      this.messagesInner().appendChild(round)
    }
    this.scrollToBottom()
  }

  // After a send: insert the tool ledger + assistant turn INTO the
  // currently-open round (the user-only round we appended above).
  appendAssistantRound(toolMessages, assistantPayload) {
    const inner = this.messagesInner()
    const round = inner.querySelector('[data-open-round="1"]')
    const ledger = toolMessages?.length ? buildToolLedger(toolMessages) : null
    const assistantRow = assistantPayload ? buildMessageRow(assistantPayload) : null

    if (round) {
      if (ledger) round.appendChild(ledger)
      if (assistantRow) round.appendChild(assistantRow)
      round.removeAttribute("data-open-round")
    } else {
      if (ledger) inner.appendChild(ledger)
      if (assistantRow) inner.appendChild(assistantRow)
    }
    this.scrollToBottom()
  }

  appendErrorMessage(text) {
    const row = buildMessageRow({ role: "system", content: text })
    if (row) this.messagesInner().appendChild(row)
    this.scrollToBottom()
  }

  // ---------- UI state helpers ----------

  showRateBanner() {
    if (!this.hasRateBannerTarget) return
    this.rateBannerTarget.classList.remove("hidden")
    setTimeout(() => this.rateBannerTarget.classList.add("hidden"), 30_000)
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  // The scrollable column has `.chat-messages-inner` as its content
  // container. All row appends target this inner element.
  messagesInner() {
    if (!this.hasMessagesTarget) return null
    let inner = this.messagesTarget.querySelector(".chat-messages-inner")
    if (!inner) {
      inner = document.createElement("div")
      inner.className = "chat-messages-inner"
      this.messagesTarget.appendChild(inner)
    }
    return inner
  }

  clearMessages() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.innerHTML = '<div class="chat-messages-inner"></div>'
  }

  // Cold-start markup mirrors app/views/chat_pages/_cold_start.html.erb
  // so a fresh "New chat" click doesn't need a page reload.
  coldStartHtml() {
    const suggestions = [
      { icon: "trend", tone: "",       q: "How are my campaigns this week?",        h: "Spend, ROAS, comparison vs last week" },
      { icon: "bar",   tone: "green",  q: "Top 5 posts by engagement this month",  h: "Ranked with deltas" },
      { icon: "warn",  tone: "gold",   q: "Anything off-track right now?",          h: "Pacing, fatigue, paused ads" },
      { icon: "zap",   tone: "accent", q: "Why didn't my last autoboost trigger?",  h: "Walk through the conditions" },
    ]
    const items = suggestions.map((s) =>
      `<button type="button" class="chat-cold-suggestion" data-action="click->chat-panel#pickSuggestion" data-prompt="${this.escape(s.q)}">
        <div class="icon ${s.tone}"></div>
        <div class="text"><div class="q">${this.escape(s.q)}</div><div class="h">${this.escape(s.h)}</div></div>
      </button>`,
    ).join("")
    return `<div class="chat-cold-start">
      <div class="chat-cold-start-mark"></div>
      <h2 class="chat-cold-start-title">Ask <em>AutoBoost</em> anything about your data.</h2>
      <p class="chat-cold-start-sub">Campaigns, posts, ads, autoboost triggers, account health — pulled live from Meta and your AutoBoost setup.</p>
      <div class="chat-cold-suggestions">${items}</div>
    </div>`
  }

  // Stimulus auto-binds data-action attributes that exist when an
  // element is added to the DOM, so the suggestion buttons will work
  // without manual binding. This is a no-op stub kept for clarity.
  bindColdStart() {
    // Intentionally empty — Stimulus handles delegation via MutationObserver.
  }

  escape(text) {
    const d = document.createElement("div")
    d.textContent = text == null ? "" : String(text)
    return d.innerHTML
  }
}
